
import { openDB } from "idb";
import { set } from "idb-keyval";
import { useRef, useState, useCallback, useEffect } from "react";
import { isGuid } from "../App";

// --- ArrayBuffer <-> Base64 helpers ---
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useVersionedIndexedDBBackup(dbName, storeNames, { onRestore } = {}) {
  const fileHandleRef = useRef(null);
  const firefoxFileData = useRef(null);
  const saveTimeoutRef = useRef(null);
  const storeNamesRef = useRef(storeNames);
  storeNamesRef.current = storeNames;
  const dbNameRef = useRef(dbName);
  dbNameRef.current = dbName;

  const supportsFSAccess =
    "showOpenFilePicker" in window &&
    "showSaveFilePicker" in window;

  const MAX_VERSIONS = 3;
  const THROTTLE_MS = 2000;

  const ASKED_KEY = `${dbNameRef.current}-asked-local-backup`;

  const HANDLE_DB_NAME = `${dbNameRef.current}-backup-handle`;
  const HANDLE_STORE = "handles";

  const [hasBackupFileState, setHasBackupFileState] = useState(false);
  const [undoAvailableState, setUndoAvailableState] = useState(false);
  const [redoAvailableState, setRedoAvailableState] = useState(false);
  const [supportsFSAccessState, setSupportsFSAccessState] = useState(supportsFSAccess);

  useEffect(() => {
    refreshStatus();
  }, []);

  /* -------------------------------------------------- */
  /*  File Handle Persistence                          */
  /* -------------------------------------------------- */

  async function getHandleDB() {
    return openDB(HANDLE_DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(HANDLE_STORE)) {
          db.createObjectStore(HANDLE_STORE);
        }
      },
    });
  }

  async function closeBackupFile() {
    fileHandleRef.current = null;
    const handleDB = await getHandleDB();
    await handleDB.delete(HANDLE_STORE, "backupFile");
    await refreshStatus();
  }

  async function chooseBackupFile(existing) {
    if (!supportsFSAccess) {
      return chooseBackupFileFirefox();
    }
    // Prompt user: create new or open existing
    alert(existing ? 
      "Please select your existing backup file to continue saving to it. Backup files are stored locally on your device and contain a history of your data. They are not uploaded anywhere and can be deleted at any time. These files are in JSON format." : 
      "Please select a location to create your backup file. You will continue saving to this file automatically in the future."
    );
    let handle;
    if(existing){
      // Try to let user pick an existing file
      [handle] = await window.showOpenFilePicker({
        types: [
          {
            description: "JSON Files",
            accept: { "application/json": [".json"] },
          },
        ],
        excludeAcceptAllOption: false,
        multiple: false,
      });

      // Request read/write permission explicitly
      const permission = await handle.requestPermission({ mode: "readwrite" });

      if (permission !== "granted") {
        throw new Error("Write permission not granted.");
      }
    }else{
      handle = await window.showSaveFilePicker({
        suggestedName: `${dbNameRef.current}-history.json`,
        types: [
          {
            description: "JSON Files",
            accept: { "application/json": [".json"] },
          },
        ],
      });
    }

    console.log("BACKUP - User selected backup file:", handle);

    fileHandleRef.current = handle;

    const handleDB = await getHandleDB();
    await handleDB.put(HANDLE_STORE, handle, "backupFile");

    markAskedToBackup();

    await syncOnFileOpen({ mergeOnChoose: true });

    await refreshStatus();
  }

  async function chooseBackupFileFirefox() {
    return new Promise((resolve, reject) => {

      alert("Please select a backup file previously created by INSTRUCT. Backup files are stored locally on your device and contain a history of your data. They are not uploaded anywhere and can be deleted at any time. These files are in JSON format.");

      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".json";

      input.onchange = async () => {

        const file = input.files[0];
        if (!file) return reject();

        const text = await file.text();
        firefoxFileData.current = text;

        fileHandleRef.current = {
          firefox: true,
          name: file.name
        };

        await syncOnFileOpen({ mergeOnChoose: true });
        await refreshStatus();

        resolve();
      };

      input.click();
    });
  }

async function loadBackupFile() {
  const handleDB = await getHandleDB();
  const handle = await handleDB.get(HANDLE_STORE, "backupFile");

  console.log("BACKUP - Loaded backup file handle from IndexedDB:", handle);

  if (!handle) {
    fileHandleRef.current = null;
    await refreshStatus();
    return;
  }

  const permission =
    (await handle.queryPermission({ mode: "readwrite" })) ||
    (await handle.requestPermission({ mode: "readwrite" }));

  if (permission === "granted") {
    fileHandleRef.current = handle;
    await syncOnFileOpen({mergeOnChoose: false});
  } else {
    fileHandleRef.current = null;
  }

  await refreshStatus();
}

async function databaseHasContent() {
  const db = await openDB(dbNameRef.current);
  for (const storeName of storeNamesRef.current) {
    const count = await db.count(storeName);
    if (count > 0) return true;
  }
  return false;
}

async function loadLatestSnapshotIntoDB({ merge = false } = {}) {
  const historyFile = await readHistoryFile();
  if (!historyFile.versions.length) return;

  const latestIndex = historyFile.versions.length - 1;
  await restoreByIndex(latestIndex, merge);
}

async function syncOnFileOpen({ mergeOnChoose = false }) {
  const historyFile = await readHistoryFile();
  const dbHasData = await databaseHasContent();

  // CASE 1 — file empty → snapshot DB into file
  if (!historyFile.versions.length) {
    if (dbHasData) {
      await writeSnapshot();
    }
    return;
  }

  // CASE 2 — choosing file → merge
  if (mergeOnChoose) {
    await loadLatestSnapshotIntoDB({ merge: true });
    return;
  }

  // CASE 3 — reloading file → replace DB
  await loadLatestSnapshotIntoDB({ merge: false });
}

  /* -------------------------------------------------- */
  /*  Asked-To-Backup Tracking                         */
  /* -------------------------------------------------- */

  function hasAskedToBackup() {
    return localStorage.getItem(ASKED_KEY) === "true";
  }

  function markAskedToBackup() {
    localStorage.setItem(ASKED_KEY, "true");
  }

  /* -------------------------------------------------- */
  /*  History File Management                          */
  /* -------------------------------------------------- */

  function defaultHistory() {
    return {
      meta: {
        dbName: dbNameRef.current,
        created: Date.now(),
        currentIndex: -1,
      },
      versions: [],
    };
  }

  async function readHistoryFile() {

    if (fileHandleRef.current?.firefox) {

      try {
        return JSON.parse(firefoxFileData.current);
      } catch {
        return defaultHistory();
      }

    }

    try {
      const file = await fileHandleRef.current.getFile();
      const text = await file.text();
      return JSON.parse(text);

    } catch {
      return defaultHistory();
    }
  }

  async function writeHistoryFile(historyFile) {
    if(fileHandleRef.current?.firefox || !supportsFSAccess) {
      return;
    }
    const writable = await fileHandleRef.current.createWritable();
    //await writable.write(JSON.stringify(historyFile, null, 2)); // pretty print for debugging
    await writable.write(JSON.stringify(historyFile));
    await writable.close();
  }

  async function writeHistoryFileFirefox(historyFile) {

    alert("Due to browser limitations, we cannot save directly to your backup file in certain browsers. Instead, we will prompt you to download an updated backup file. Backup files are stored locally on your device and contain a history of your data. They are not uploaded anywhere and can be deleted at any time. These files are in JSON format. Click OK to continue.");
    // 1️⃣ Read current history
    let currentHistory = await readHistoryFile();
    if (!currentHistory.meta) currentHistory = defaultHistory();

    // 2️⃣ Create snapshot from IndexedDB
    const { snapshotData, blobs } = await createSnapshot();
    const newHash = await hashSnapshot(snapshotData);

      console.log("BACKUP - New snapshot hash:", newHash, " Snapshot data:", snapshotData, " Blobs:", blobs);

    // 3️⃣ Check for duplicate
    const latest = currentHistory.versions[currentHistory.versions.length - 1];
    if (latest && latest.hash === newHash) {
      // No change, skip download
      return;
    }

    // 4️⃣ Truncate future versions if needed
    if (currentHistory.meta.currentIndex < currentHistory.versions.length - 1) {
      currentHistory.versions = currentHistory.versions.slice(
        0,
        currentHistory.meta.currentIndex + 1
      );
    }

    // 5️⃣ Add new version
    currentHistory.versions.push({
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      hash: newHash,
      data: snapshotData,
    });

    // 6️⃣ Trim to MAX_VERSIONS
    if (currentHistory.versions.length > MAX_VERSIONS) {
      currentHistory.versions = currentHistory.versions.slice(-MAX_VERSIONS);
    }

    // 7️⃣ Update meta and blobs
    currentHistory.meta.currentIndex = currentHistory.versions.length - 1;
    currentHistory.blobs = blobs;

    // 8️⃣ Download file
    if (fileHandleRef.current?.firefox || !supportsFSAccess) {
      const blob = new Blob(
        [JSON.stringify(currentHistory)],
        { type: "application/json" }
      );

      const url = URL.createObjectURL(blob);

      const now = new Date();
      const timestamp =
        now.getFullYear() + "-" +
        String(now.getMonth() + 1).padStart(2, "0") + "-" +
        String(now.getDate()).padStart(2, "0") + "_" +
        String(now.getHours()).padStart(2, "0") + "-" +
        String(now.getMinutes()).padStart(2, "0") + "-" +
        String(now.getSeconds()).padStart(2, "0");

      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_${timestamp}.json`;
      a.click();

      URL.revokeObjectURL(url);
      return;
    }
  }

  async function createSnapshot() {
    const db = await openDB(dbNameRef.current);
    const historyFile = await readHistoryFile();

    if (!historyFile.blobs) historyFile.blobs = {};

    const snapshotData = {};

    for (const storeName of storeNamesRef.current) {
      const tx = db.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);

      const allRecords = await store.getAll();
      const processedRecords = [];

      for (const record of allRecords) {
        const copy = structuredClone(record);
        // Handle media blobs
        if (storeName === "media" && copy.file instanceof Blob) {
          const hash = await hashBlob(copy.file);
          if (!historyFile.blobs[hash]) {
            const ab = await compressToArrayBuffer(copy.file);
            historyFile.blobs[hash] = {
              type: copy.file.type,
              data: arrayBufferToBase64(ab)
            };
          }
          copy.fileHash = hash;
          delete copy.file;
        }
        processedRecords.push(copy);
      }
      snapshotData[storeName] = processedRecords;
      await tx.done;
    }
    return { snapshotData, blobs: historyFile.blobs };
  }

  // async function blobToBase64(blob) {
  //   const buffer = await blob.arrayBuffer();
  //   const bytes = new Uint8Array(buffer);

  //   let binary = "";
  //   for (let i = 0; i < bytes.length; i++) {
  //     binary += String.fromCharCode(bytes[i]);
  //   }

  //   return btoa(binary);
  // }

  // async function compressBlob(blob) {
  //   const cs = new CompressionStream("gzip");
  //   const compressedStream = blob.stream().pipeThrough(cs);
  //   return await new Response(compressedStream).blob();
  // }

  async function compressToArrayBuffer(blob) {
    const cs = new CompressionStream("gzip");
    const compressedStream = blob.stream().pipeThrough(cs);
    // Read the compressed stream directly to an ArrayBuffer
    const ret = await new Response(compressedStream).arrayBuffer();
    return ret;
  }

  // function base64ToBlob(base64, type) {
  //   const binary = atob(base64);
  //   const bytes = new Uint8Array(binary.length);

  //   for (let i = 0; i < binary.length; i++) {
  //     bytes[i] = binary.charCodeAt(i);
  //   }

  //   return new Blob([bytes], { type });
  // }

  async function decompressArrayBufferToBlob(arrayBuffer, mimeType = "application/octet-stream") {
    // Convert ArrayBuffer into a Blob so we can stream it
    const compressedBlob = new Blob([arrayBuffer]);

    // Create decompression stream
    const ds = new DecompressionStream("gzip");

    // Pipe through decompression
    const decompressedStream = compressedBlob.stream().pipeThrough(ds);

    // Convert back to Blob
    return await new Response(decompressedStream).blob().then(
      blob => new Blob([blob], { type: mimeType })
    );
  }

  async function hashBlob(blob) {
    const buffer = await blob.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }

  async function hashSnapshot(snapshot) {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(snapshot));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  }

  async function hashJSON(obj) {
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(obj));
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
  }

  async function writeSnapshot() {
    if (!hasBackupFileState) return;

    const historyFile = await readHistoryFile();
    const { snapshotData, blobs } = await createSnapshot();
    const newHash = await hashSnapshot(snapshotData);

    console.log("BACKUP - New snapshot hash:", newHash, " Snapshot data:", snapshotData, " Blobs:", blobs);

    const latest = historyFile.versions[historyFile.versions.length - 1];

    // 🔥 Skip duplicate snapshot
    if (latest && latest.hash === newHash) {
      console.log("BACKUP - Snapshot unchanged since last version, skipping backup write.");
      return;
    }

    if (historyFile.meta.currentIndex < historyFile.versions.length - 1) {
      historyFile.versions = historyFile.versions.slice(
        0,
        historyFile.meta.currentIndex + 1
      );
    }

    historyFile.versions.push({
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      hash: newHash, // 🔥 store hash
      data: snapshotData,
    });

    if (historyFile.versions.length > MAX_VERSIONS) {
      historyFile.versions = historyFile.versions.slice(-MAX_VERSIONS);
    }

    historyFile.meta.currentIndex = historyFile.versions.length - 1;
    historyFile.blobs = blobs;

    await garbageCollectBlobs(historyFile);
    await writeHistoryFile(historyFile);
    await refreshStatus();
  }

  function scheduleSnapshot() {
    console.log("BACKUP - scheduleSnapshot called, hasBackupFileState:", hasBackupFileState);
    if(!hasBackupFileState) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(writeSnapshot, THROTTLE_MS);
  }

  async function garbageCollectBlobs(historyFile) {
    if(!historyFile.versions?.length || !historyFile.blobs) return;
    const referenced = new Set();

    for (const version of historyFile.versions) {
      if (!version.data) continue;
      for (const storeName of Object.keys(version.data)) {
        if (storeName !== "media") continue;
        const items = Array.isArray(version.data[storeName]) ? version.data[storeName] : [];
        for (const item of items) {
          if (item.fileHash) {
            referenced.add(item.fileHash);
          }
        }
      }
    }

    for (const hash of Object.keys(historyFile.blobs)) {
      if (!referenced.has(hash)) {
        delete historyFile.blobs[hash];
      }
    }
  }

  async function restoreByIndex(index, merge = false) {
    const historyFile = await readHistoryFile();
    const version = historyFile.versions[index];
    if (!version) return;

    console.log("BACKUP - Restoring version index:", index, " merge:", merge, " with hash:", version.hash, " and data:", version.data);

    const db = await openDB(dbNameRef.current);

    // for (const storeName of storeNamesRef.current) {
    //   const tx = db.transaction(storeName, "readwrite");
    //   const store = tx.objectStore(storeName);

    //   const items = version.data.snapshotData && Array.isArray(version.data.snapshotData[storeName]) ? version.data.snapshotData[storeName] : 
    //         Array.isArray(version.data[storeName]) ? version.data[storeName] : [];

    //   if (!merge) {
    //     await store.clear();
    //     for (const item of items) {
    //       const copy = structuredClone(item);
    //       // Rehydrate media blobs
    //       if (storeName === "media" && copy.fileHash) {
    //         if (historyFile.blobs && historyFile.blobs[copy.fileHash]) {
    //           const blobData = historyFile.blobs[copy.fileHash];
    //           // decode base64 to ArrayBuffer
    //           const ab = base64ToArrayBuffer(blobData.data);
    //           copy.file = await decompressArrayBufferToBlob(
    //             ab,
    //             blobData.type
    //           );
    //         } else {
    //           copy.file = null;
    //         }
    //         delete copy.fileHash;
    //       }
    //       await store.put(copy);
    //     }
    //   } else {
    //     // Merge mode: only add items that do not exist
    //     const allExisting = await store.getAll();
    //     const existingIds = new Set(allExisting.map(e => e.Id));
    //     const existingAutoIds = new Set(allExisting.map(e => e.id));
    //     for (const item of items) {
    //       const copy = structuredClone(item);
    //       // Rehydrate media blobs
    //       if (storeName === "media" && copy.fileHash) {
    //         if (historyFile.blobs && historyFile.blobs[copy.fileHash]) {
    //           const blobData = historyFile.blobs[copy.fileHash];
    //           const ab = base64ToArrayBuffer(blobData.data);
    //           copy.file = await decompressArrayBufferToBlob(
    //             ab,
    //             blobData.type
    //           );
    //         } else {
    //           copy.file = null;
    //         }
    //         delete copy.fileHash;
    //       }
    //       if(isGuid(copy.id)){
    //         try{
    //           await store.add(copy);
    //           existingAutoIds.add(copy.id);
    //         }catch(e){
    //           console.error("Error merging item into store:", e, " Item:", copy);
    //         }
    //       }else{
    //         try{
    //           if (!existingIds.has(copy.Id)) {
    //             if(existingAutoIds.has(copy.id)){
    //               copy.id = existingAutoIds.size > 0 ? Math.max(...existingAutoIds) + 1 : 1; // assign new id if conflict
    //             }
    //             await store.add(copy);
    //             existingIds.add(copy.Id);
    //             existingAutoIds.add(copy.id);
    //           }
    //         }catch(e){
    //           console.error("Error merging item into store:", e, " Item:", copy);
    //         }
    //       }
    //     }
    //   }

    //   await tx.done;
    // }

    for (const storeName of storeNamesRef.current) {
      // Rehydrate all items BEFORE opening the transaction
      const items = version.data.snapshotData && Array.isArray(version.data.snapshotData[storeName])
        ? version.data.snapshotData[storeName]
        : Array.isArray(version.data[storeName]) ? version.data[storeName] : [];

      const rehydratedItems = [];
      for (const item of items) {
        const copy = structuredClone(item);
        if (storeName === "media" && copy.fileHash) {
          if (historyFile.blobs && historyFile.blobs[copy.fileHash]) {
            const blobData = historyFile.blobs[copy.fileHash];
            const ab = base64ToArrayBuffer(blobData.data);
            copy.file = await decompressArrayBufferToBlob(ab, blobData.type);
          } else {
            copy.file = null;
          }
          delete copy.fileHash;
        }
        rehydratedItems.push(copy);
      }

      // Now open the transaction and do all writes synchronously (no awaits on async work)
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);

      if (!merge) {
        await store.clear();
        for (const copy of rehydratedItems) {
          await store.put(copy);
        }
      } else {
        const allExisting = await store.getAll();
        const existingIds = new Set(allExisting.map(e => e.Id));
        const existingAutoIds = new Set(allExisting.map(e => e.id));

        console.log("BACKUP - Merging items into store:", storeName, " allExisting:", allExisting, " rehydratedItems:", rehydratedItems);

        for (const copy of rehydratedItems) {
          try {
            if (isGuid(copy.id)) {
              await store.add(copy);
              existingAutoIds.add(copy.id);
            } else {
              if (!existingIds.has(copy.Id)) {
                if (existingAutoIds.has(copy.id)) {
                  copy.id = existingAutoIds.size > 0 ? Math.max(...existingAutoIds) + 1 : 1;
                }
                await store.add(copy);
                existingIds.add(copy.Id);
                existingAutoIds.add(copy.id);
              }
            }
          } catch (e) {
            console.error("BACKUP - Error merging item into store:", e, " Item:", copy);
          }
        }
      }

      await tx.done;
    }

    historyFile.meta.currentIndex = index;
    historyFile.meta.dbHash = version.hash;

    await writeHistoryFile(historyFile);
    await refreshStatus();
    if (typeof onRestore === "function") {
      onRestore();
    }
  }

  async function undo() {
    const historyFile = await readHistoryFile();
    const newIndex = historyFile.meta.currentIndex - 1;
    if (newIndex >= 0) {
      await restoreByIndex(newIndex);
    }
  }

  async function redo() {
    const historyFile = await readHistoryFile();
    const newIndex = historyFile.meta.currentIndex + 1;
    if (newIndex < historyFile.versions.length) {
      await restoreByIndex(newIndex);
    }
  }

  async function listVersions() {
    const historyFile = await readHistoryFile();
    return historyFile.versions.map((v, i) => ({
      id: v.id,
      timestamp: v.timestamp,
      index: i,
      isCurrent: i === historyFile.meta.currentIndex,
    }));
  }

  const refreshStatus = useCallback(async () => {
    const supportsFSAccess =
      "showOpenFilePicker" in window &&
      "showSaveFilePicker" in window;
      setSupportsFSAccessState(supportsFSAccess);
    if (!fileHandleRef.current || !supportsFSAccess) {
        setHasBackupFileState(false);
        setUndoAvailableState(false);
        setRedoAvailableState(false);
        return;
    }

    const permission = await fileHandleRef.current.queryPermission({
        mode: "readwrite",
    });

    const hasFile = permission === "granted";
    setHasBackupFileState(hasFile);

    if (!hasFile) {
        setUndoAvailableState(false);
        setRedoAvailableState(false);
        return;
    }

    const historyFile = await readHistoryFile();

    setUndoAvailableState(historyFile.meta.currentIndex > 0);
    setRedoAvailableState(
        historyFile.meta.currentIndex <
        historyFile.versions.length - 1
    );
  }, []);

  return {
    hasBackupFile: hasBackupFileState,
    undoAvailable: undoAvailableState,
    redoAvailable: redoAvailableState,
    supportsFSAccess: supportsFSAccessState,
    chooseBackupFile,
    loadBackupFile,
    closeBackupFile,
    scheduleSnapshot,
    writeHistoryFileFirefox,
    undo,
    redo,
    restoreByIndex,
    listVersions,
    hasAskedToBackup,
    markAskedToBackup,
  };
}